import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { Liga } from '../types/liga'
import { useAuth } from '../context/AuthContext'
import { useProfile } from '../context/ProfileContext'
import {
  CRIAR_SEGUIDOR_MUTATION,
  DELETAR_SEGUIDOR_MUTATION,
  CriarSeguidorResponse,
  SEGUIDOR_USUARIO_LIGA,
  SeguidorUsuarioLigaData,
  SeguidorUsuarioLigaVars,
} from '../services/seguidorService'

interface LeagueCardProps {
  liga: Liga
}

const LeagueCard: React.FC<LeagueCardProps> = ({ liga }) => {
  const navigate = useNavigate()
  const auth = useAuth()
  const { currentProfile } = useProfile()
  const userObj =
    (auth?.user as any) ||
    (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null)
  const seguidorUserId = userObj?.atleta?.id ?? ''
  const isAdmin = userObj?.role === 'ADMIN'
  const isLigaProfile = currentProfile?.tipo === 'LIGA'

  const [seguir, { loading: loadingSeguir }] = useMutation<{
    criarSeguidor: CriarSeguidorResponse
  }, { input: any }>(CRIAR_SEGUIDOR_MUTATION)
  const [deletar, { loading: loadingDeletar }] = useMutation(DELETAR_SEGUIDOR_MUTATION)
  const [followId, setFollowId] = useState<string | null>(null)
  const [erro, setErro] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { data: seguidorData, refetch: refetchSeguidor } = useQuery<SeguidorUsuarioLigaData, SeguidorUsuarioLigaVars>(
    SEGUIDOR_USUARIO_LIGA,
    {
      variables: { seguidorId: seguidorUserId, seguidoLigaId: liga?.id || '' },
      skip: !seguidorUserId || !liga?.id,
      fetchPolicy: 'network-only',
    }
  )

  useEffect(() => {
    if (seguidorData?.seguidorPorSeguidorIdSeguidoLigaId?.id) {
      setFollowId(seguidorData.seguidorPorSeguidorIdSeguidoLigaId.id)
    } else {
      setFollowId(null)
    }
  }, [seguidorData])

  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      const dropdown = document.getElementById(`dropdown-league-${liga.id}`)
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        dropdown &&
        !dropdown.contains(e.target as Node)
      ) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen, liga.id])

  const handleFollow = async () => {
    if (!seguidorUserId || !liga?.id) return
    setErro('')
    try {
      if (followId) {
        await deletar({ variables: { id: followId } })
        setFollowId(null)
      } else {
        setFollowId('temp')
        const { data } = await seguir({
          variables: {
            input: {
              seguidorId: seguidorUserId,
              seguidoAtletaId: null,
              seguidoTorneioId: null,
              seguidoLigaId: liga.id,
            },
          },
        })
        const id = data?.criarSeguidor.id
        if (id) setFollowId(id)
        else setFollowId(null)
      }
      setMenuOpen(false)
    } catch (e) {
      setErro('Não foi possível atualizar. Tente novamente.')
      if (!followId || followId === 'temp') setFollowId(null)
      // Refetch da query para garantir sincronização do estado
      if (refetchSeguidor) {
        refetchSeguidor()
      }
    }
  }

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen((v) => !v)
  }

  const handleCardClick = () => {
    navigate(`/league/${liga.id}`)
  }

  const handleVerPerfil = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/league/${liga.id}`)
    setMenuOpen(false)
  }

  const handleVerJogadores = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/league/${liga.id}#jogadores`)
    setMenuOpen(false)
  }

  const handleVerTorneios = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/league/${liga.id}#torneios`)
    setMenuOpen(false)
  }

  return (
    <div className="col-lg-6 col-md-12 pe-2 ps-2">
      <div 
        className="card p-3 bg-white w-100 hover-card border-0 shadow-xss rounded-xxl mb-3 position-relative" 
        style={{ height: '400px', cursor: 'pointer' }}
        onClick={handleCardClick}
      >
        <div className="card-image w-100 position-relative" style={{ height: '200px' }}>
          <img 
            src={liga.bannerUrl || '/assets/images/comingsoon.svg'} 
            alt={liga.nome} 
            className="w-100 rounded-3"
            style={{ height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="card-body d-flex ps-0 pe-0 pb-0 position-relative">
          <figure className="avatar position-relative w75 z-index-1 me-3" style={{ marginTop: '-40px' }}>
            <img
              src={liga.imagemUrl || '/assets/images/user.png'}
              alt={liga.nome}
              className="p-1 bg-white rounded-xl w-100"
            />
          </figure>
          <div className="flex-grow-1">
            <h4 className="fw-700 lh-3 font-xss mb-1">
              {liga.nome}
            </h4>
            {liga.username && (
              <span className="d-flex font-xssss fw-500 mt-1 lh-3 text-primary">
                @{liga.username}
              </span>
            )}
          </div>
        </div>
        <div className="position-absolute bottom-0 end-0 m-3">
          <div className="dropdown">
            <button
              id={`dropdown-league-${liga.id}`}
              className="btn bg-white  rounded-3 text-grey-700 shadow-sm"
              onClick={toggleMenu}
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              opções
            </button>
            <div
              ref={menuRef}
              className={`dropdown-menu dropdown-menu-end p-2 rounded-xxl border-0 shadow-lg ${menuOpen ? 'show' : ''}`}
              aria-labelledby={`dropdown-league-${liga.id}`}
              style={{ minWidth: 200, bottom: '100%', marginBottom: '8px' }}
            >
              <button className="dropdown-item" onClick={handleVerPerfil}>
                Ver perfil da Liga
              </button>
              <button className="dropdown-item" onClick={handleVerJogadores}>
                Ver jogadores da Liga
              </button>
              <button className="dropdown-item" onClick={handleVerTorneios}>
                Ver torneios da Liga
              </button>
              {!isAdmin && !isLigaProfile && seguidorUserId && (
                <button 
                  className="dropdown-item" 
                  onClick={handleFollow}
                  disabled={loadingSeguir || loadingDeletar || followId === 'temp'}
                >
                  {loadingSeguir || loadingDeletar
                    ? 'Aguarde...'
                    : followId
                    ? 'Deixar de Seguir Liga'
                    : 'Seguir Liga'}
                </button>
              )}
            </div>
          </div>
        </div>
        {erro && (
          <div className="alert alert-danger mt-2 mx-3" role="alert">
            {erro}
          </div>
        )}
      </div>
    </div>
  )
}

export default LeagueCard
